import { useEffect } from "react";

const SITE_ORIGIN = "https://jaikmarketing.com";

const toAbsolute = (url?: string) => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
};

// Utility routes that should stay out of search results.
const NOINDEX_PREFIXES = ["/thank-you", "/book/thanks"];

type SEOHeadProps = {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
  noindex?: boolean;
};

const SEOHead = ({ title, description, canonical, ogImage, ogType = "website", jsonLd, noindex }: SEOHeadProps) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    // Default to the current route so every page self-canonicalizes to the
    // primary domain instead of inheriting the homepage canonical.
    const routePath =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : "/";
    const absoluteUrl = toAbsolute(canonical ?? routePath);

    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const shouldNoindex = noindex ?? NOINDEX_PREFIXES.some((p) => path === p || path.startsWith(p));
    setMeta("robots", shouldNoindex ? "noindex, nofollow" : "index, follow");

    setMeta("description", description);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", ogType, true);
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    // og:url should self-reference the current route
    if (absoluteUrl) setMeta("og:url", absoluteUrl, true);

    const absoluteImage = toAbsolute(ogImage);
    if (absoluteImage) {
      setMeta("og:image", absoluteImage, true);
      setMeta("twitter:image", absoluteImage);
      setMeta("twitter:card", "summary_large_image");
    }

    // Canonical
    if (absoluteUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = absoluteUrl;
    }

    // JSON-LD
    if (jsonLd) {
      const existingScript = document.querySelector('script[data-seo-jsonld]');
      if (existingScript) existingScript.remove();
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.querySelector('script[data-seo-jsonld]');
      if (script) script.remove();
    };
  }, [title, description, canonical, ogImage, ogType, jsonLd, noindex]);

  return null;
};

export default SEOHead;
